import { useState, useRef } from "react";
import AudioEngine from "./audioEngine";

const SYS = "#38bdf8";
const SYS_DIM = "rgba(56,189,248,0.12)";
const SYS_BORDER = "rgba(56,189,248,0.25)";

const CAT = {
  work:     { color:"#3b82f6", bg:"rgba(59,130,246,0.10)",  icon:"💼", label:"Work" },
  personal: { color:"#a855f7", bg:"rgba(168,85,247,0.10)", icon:"🌱", label:"Personal" },
  health:   { color:"#22c55e", bg:"rgba(34,197,94,0.10)",  icon:"💪", label:"Health" },
  learning: { color:"#f59e0b", bg:"rgba(245,158,11,0.10)", icon:"📚", label:"Learning" },
  creative: { color:"#ec4899", bg:"rgba(236,72,153,0.10)", icon:"🎨", label:"Creative" },
  other:    { color:"#94a3b8", bg:"rgba(148,163,184,0.10)", icon:"⚡", label:"Other" },
};

const METRICS = {
  time:       {icon:"⏱", label:"Time Spent",      unit:"",      isDecimal:false},
  pages:      {icon:"📄", label:"Pages Read",      unit:"pages", isDecimal:false},
  reps:       {icon:"🔁", label:"Reps / Sets",     unit:"reps",  isDecimal:false},
  km:         {icon:"🏃", label:"Distance (km)",   unit:"km",    isDecimal:true },
  words:      {icon:"✍️", label:"Words Written",   unit:"words", isDecimal:false},
  tasks_done: {icon:"✅", label:"Completions",     unit:"done",  isDecimal:false},
  custom:     {icon:"🎯", label:"Custom",           unit:"",      isDecimal:true },
};

// ─── Onboarding ───────────────────────────────────────────────────────────────
export default function OnboardingFlow({playerName, onComplete, onSkip}){
  const [step,setStep]=useState(0); // 0=intro,1=volume,2=category,3=target,4=goal,5=log
  const [cat,setCat]=useState("work");
  const [title,setTitle]=useState("");
  const [metric,setMetric]=useState("custom");
  const [customUnit,setCustomUnit]=useState("");
  const [goalAmt,setGoalAmt]=useState("");
  const [goalH,setGoalH]=useState("");
  const [goalM,setGoalM]=useState("");
  const [logAmt,setLogAmt]=useState("");
  const [logH,setLogH]=useState("");
  const [logM,setLogM]=useState("");
  const [logDone,setLogDone]=useState(false); // after logging, show add another vs enter
  const [savedTasks,setSavedTasks]=useState([]);
  const [vol,setVol]=useState(()=>parseFloat(localStorage.getItem('arise_volume') ?? '0.7'));
  const volPreviewTimer=useRef(null);

  function handleVolSlider(e){
    const v=parseFloat(e.target.value);
    setVol(v);
    AudioEngine.setVolume(v);
    clearTimeout(volPreviewTimer.current);
    if(v>0){
      volPreviewTimer.current=setTimeout(()=>{AudioEngine.click();},150);
    }
  }

  function nextStep(){setStep(s=>Math.min(s+1,5));}

  function buildCurrentTask(){
    if(!title.trim()) return null;
    const task={
      id:`onboard-${Date.now()}-${Math.random().toString(36).slice(2,9)}`,
      createdAt:new Date().toISOString(),
      title:title.trim(),
      desc:"",
      cat,
      status:"In Progress",
      progress:0,
      logs:[],
      total:0,
      metric,
      customUnit:customUnit||"",
      goals:metric==="time"
        ?(goalH||goalM)?{weekly:{h:goalH||"0",m:goalM||"0"}}:{}
        :goalAmt?{weekly:{amount:goalAmt}}:{},
      xp:10,
    };
    if(logAmt||(metric==="time"&&(logH||logM))){
      const totalMins=metric==="time"?(parseInt(logH||0)*60+parseInt(logM||0)):null;
      const entry={
        id:`log-${Date.now()}`,
        date:new Date().toISOString(),
        metric,
        value:metric==="time"?{totalMins}:{amount:parseFloat(logAmt)||1},
        status:"In Progress",
        progress:0,
        note:"First log! 🎉",
      };
      task.logs=[entry];
      task.total=metric==="time"?(totalMins/60):parseFloat(logAmt)||1;
    }
    return task;
  }

  const savedTasksRef=useRef([]);

  function resetTargetForm(){
    setCat("work"); setTitle(""); setMetric("custom");
    setCustomUnit(""); setGoalAmt(""); setGoalH(""); setGoalM(""); setLogAmt("");
    setLogH(""); setLogM(""); setLogDone(false);
    setStep(1);
  }

  function handleAddAnother(){
    const task=buildCurrentTask();
    if(task){
      savedTasksRef.current=[...savedTasksRef.current,task];
      setSavedTasks([...savedTasksRef.current]);
    }
    resetTargetForm();
  }

  function handleFinish(skipped){
    if(skipped){
      onComplete(savedTasksRef.current.length>0?savedTasksRef.current:null, true);
      return;
    }
    const task=buildCurrentTask();
    const allTasks=task?[...savedTasksRef.current,task]:savedTasksRef.current;
    onComplete(allTasks.length>0?allTasks:null, false);
  }

  const stepDot=(i)=>(
    <div key={i} style={{width:i===step?20:6,height:6,borderRadius:3,
      background:i<=step?SYS:"rgba(255,255,255,0.1)",transition:"all 0.3s ease"}}/>
  );

  return(
    <div style={{position:"fixed",inset:0,background:"#000",zIndex:9500,
      display:"flex",alignItems:"center",justifyContent:"center",padding:20,
      fontFamily:"'Outfit',sans-serif"}}>
      <div style={{width:"100%",maxWidth:400}}>

        {/* Progress bar */}
        <div style={{display:"flex",gap:4,justifyContent:"center",marginBottom:32}}>
          {[0,1,2,3,4,5].map(i=>stepDot(i))}
        </div>

        {/* Step 0 — Intro */}
        {step===0&&(
          <div style={{animation:"fadeIn 0.4s ease"}}>
            <div style={{textAlign:"center",marginBottom:32}}>
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:32,fontWeight:800,
                color:SYS,textShadow:`0 0 30px ${SYS}`,marginBottom:8}}>
                WELCOME,
              </div>
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:28,fontWeight:800,
                color:"#fff",marginBottom:24}}>{playerName}</div>
              <div style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${SYS_BORDER}`,
                borderRadius:14,padding:"20px 24px",textAlign:"left"}}>
                {[
                  {icon:"🎯", text:"Set targets for things you want to do regularly"},
                  {icon:"📝", text:"Log your progress every time you do them"},
                  {icon:"⚡", text:"Earn XP, level up, and track your growth"},
                ].map((item,i)=>(
                  <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",
                    marginBottom:i<2?16:0}}>
                    <span style={{fontSize:18,marginTop:2}}>{item.icon}</span>
                    <div style={{fontSize:13,color:"#aaa",lineHeight:1.5}}>{item.text}</div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={()=>{AudioEngine.click();nextStep();}}
              style={{width:"100%",padding:"14px",background:SYS,border:"none",borderRadius:12,
                color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,cursor:"pointer"}}>
              [ LET'S GO ]
            </button>
            <div style={{textAlign:"center",marginTop:14}}>
              <span onClick={()=>handleFinish(true)}
                style={{fontFamily:"monospace",fontSize:10,color:"#64748b",cursor:"pointer",letterSpacing:1}}>
                SKIP SETUP
              </span>
            </div>
          </div>
        )}

        {/* Step 1 — Volume */}
        {step===1&&(
          <div style={{animation:"fadeIn 0.4s ease"}}>
            <div style={{fontFamily:"monospace",fontSize:10,color:SYS,letterSpacing:3,marginBottom:8}}>
              STEP 1 OF 5
            </div>
            <div style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:6}}>
              Set your volume
            </div>
            <div style={{fontSize:12,color:"#64748b",marginBottom:24}}>
              ARISE uses sound for actions like logging progress and leveling up. Drag to preview.
            </div>

            <div style={{background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
              borderRadius:12,padding:"20px 18px",marginBottom:24}}>
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:36,fontWeight:900,
                color:SYS,lineHeight:1,marginBottom:16,textAlign:"center"}}>
                {Math.round(vol*100)}%
              </div>
              <style>{`
                .onboard-vol-slider {
                  -webkit-appearance: none; appearance: none;
                  width: 100%; height: 4px; border-radius: 2px; outline: none; cursor: pointer;
                  background: linear-gradient(90deg,
                    #38bdf8 ${Math.round(vol*100)}%,
                    rgba(255,255,255,0.07) ${Math.round(vol*100)}%);
                  transition: background 0.1s;
                }
                .onboard-vol-slider::-webkit-slider-thumb {
                  -webkit-appearance: none; appearance: none;
                  width: 20px; height: 20px; border-radius: 50%;
                  background: #38bdf8; border: 2px solid rgba(56,189,248,0.6);
                  box-shadow: 0 0 8px rgba(56,189,248,0.5);
                  transition: all 0.2s; cursor: pointer;
                }
              `}</style>
              <input type="range" min="0" max="1" step="0.05" value={vol}
                onChange={handleVolSlider} className="onboard-vol-slider"/>
            </div>

            <button onClick={()=>{AudioEngine.click();nextStep();}}
              style={{width:"100%",padding:"14px",background:SYS,border:"none",borderRadius:12,
                color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,cursor:"pointer"}}>
              [ CONTINUE ]
            </button>
            <div style={{textAlign:"center",marginTop:14}}>
              <span onClick={()=>{AudioEngine.click();nextStep();}}
                style={{fontFamily:"monospace",fontSize:10,color:"#64748b",cursor:"pointer",letterSpacing:1}}>
                SKIP — you can set this later in Settings
              </span>
            </div>
          </div>
        )}

        {/* Step 2 — Category */}
        {step===2&&(
          <div style={{animation:"fadeIn 0.4s ease"}}>
            <div style={{fontFamily:"monospace",fontSize:10,color:SYS,letterSpacing:3,marginBottom:8}}>
              STEP 2 OF 5
            </div>
            <div style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:6}}>
              What area do you want to track?
            </div>
            <div style={{fontSize:12,color:"#64748b",marginBottom:24}}>Choose your first focus category</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:24}}>
              {Object.entries(CAT).map(([k,v])=>(
                <div key={k} onClick={()=>{AudioEngine.click();setCat(k)}}
                  style={{padding:"14px 16px",borderRadius:12,cursor:"pointer",
                    border:`2px solid ${cat===k?v.color:"rgba(255,255,255,0.06)"}`,
                    background:cat===k?`${v.color}15`:"rgba(255,255,255,0.02)",
                    transition:"all 0.2s"}}>
                  <div style={{fontSize:20,marginBottom:6}}>{v.icon}</div>
                  <div style={{fontSize:12,fontWeight:700,color:cat===k?v.color:"#aaa"}}>{v.label}</div>
                </div>
              ))}
            </div>
            <button onClick={nextStep}
              style={{width:"100%",padding:"14px",background:SYS,border:"none",borderRadius:12,
                color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,cursor:"pointer"}}>
              [ NEXT ]
            </button>
            <div style={{textAlign:"center",marginTop:14}}>
              <span onClick={()=>handleFinish(true)}
                style={{fontFamily:"monospace",fontSize:10,color:"#64748b",cursor:"pointer",letterSpacing:1}}>
                SKIP SETUP
              </span>
            </div>
          </div>
        )}

        {/* Step 3 — Target name */}
        {step===3&&(
          <div style={{animation:"fadeIn 0.4s ease"}}>
            <div style={{fontFamily:"monospace",fontSize:10,color:SYS,letterSpacing:3,marginBottom:8}}>
              STEP 3 OF 5
            </div>
            <div style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:6}}>
              Name your first target
            </div>
            <div style={{fontSize:12,color:"#64748b",marginBottom:24}}>What do you want to track?</div>
            <input value={title} onChange={e=>setTitle(e.target.value)}
              placeholder={`e.g. ${cat==="health"?"Gym":"Reading"}`}
              style={{width:"100%",background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                borderRadius:12,padding:"14px 16px",color:"#fff",fontSize:16,fontWeight:700,
                outline:"none",marginBottom:16,fontFamily:"'Outfit',sans-serif"}}/>
            <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:2,marginBottom:12}}>
              WHAT TO TRACK
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:24}}>
              {Object.entries(METRICS).filter(([k])=>k!=="custom").map(([k,v])=>(
                <div key={k} onClick={()=>{AudioEngine.click();setMetric(k)}}
                  style={{padding:"10px 12px",borderRadius:10,cursor:"pointer",
                    border:`1px solid ${metric===k?SYS:"rgba(255,255,255,0.06)"}`,
                    background:metric===k?SYS_DIM:"rgba(255,255,255,0.02)",
                    display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:14}}>{v.icon}</span>
                  <span style={{fontSize:11,color:metric===k?SYS:"#667"}}>{v.label}</span>
                </div>
              ))}
              <div onClick={()=>{AudioEngine.click();setMetric("custom")}}
                style={{padding:"10px 12px",borderRadius:10,cursor:"pointer",
                  border:`1px solid ${metric==="custom"?SYS:"rgba(255,255,255,0.06)"}`,
                  background:metric==="custom"?SYS_DIM:"rgba(255,255,255,0.02)",
                  display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:14}}>🎯</span>
                <span style={{fontSize:11,color:metric==="custom"?SYS:"#667"}}>Custom</span>
              </div>
            </div>
            {metric==="custom"&&(
              <input value={customUnit} onChange={e=>setCustomUnit(e.target.value)}
                placeholder="Unit name (e.g. sessions, posts)"
                style={{width:"100%",background:"rgba(255,255,255,0.03)",
                  border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                  padding:"12px 14px",color:"#fff",fontSize:13,outline:"none",marginBottom:16,
                  fontFamily:"'Outfit',sans-serif"}}/>
            )}
            <button onClick={nextStep} disabled={!title.trim()}
              style={{width:"100%",padding:"14px",background:title.trim()?SYS:"rgba(56,189,248,0.2)",
                border:"none",borderRadius:12,color:"#000",fontWeight:800,fontSize:13,
                fontFamily:"monospace",letterSpacing:2,cursor:title.trim()?"pointer":"not-allowed"}}>
              [ NEXT ]
            </button>
            <div style={{textAlign:"center",marginTop:14}}>
              <span onClick={()=>handleFinish(true)}
                style={{fontFamily:"monospace",fontSize:10,color:"#64748b",cursor:"pointer",letterSpacing:1}}>
                SKIP SETUP
              </span>
            </div>
          </div>
        )}

        {/* Step 4 — Weekly goal */}
        {step===4&&(
          <div style={{animation:"fadeIn 0.4s ease"}}>
            <div style={{fontFamily:"monospace",fontSize:10,color:SYS,letterSpacing:3,marginBottom:8}}>
              STEP 4 OF 5
            </div>
            <div style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:6}}>
              Set a weekly goal
            </div>
            <div style={{fontSize:12,color:"#64748b",marginBottom:24}}>
              How much <span style={{color:"#fff",fontWeight:700}}>{title||"this"}</span> do you want to do each week?
            </div>

            <div style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${SYS_BORDER}`,
              borderRadius:12,padding:"20px",marginBottom:24}}>
              {metric==="time"?(
                <>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:10}}>
                    WEEKLY TIME GOAL
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{flex:1,textAlign:"center"}}>
                      <input value={goalH} onChange={e=>setGoalH(e.target.value)}
                        type="number" min="0" placeholder="0"
                        style={{width:"100%",background:"rgba(255,255,255,0.03)",
                          border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                          padding:"14px",color:"#fff",fontSize:22,fontWeight:800,outline:"none",
                          fontFamily:"'Share Tech Mono',monospace",textAlign:"center"}}/>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:6}}>HOURS</div>
                    </div>
                    <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:24,color:"#64748b",paddingBottom:20}}>:</div>
                    <div style={{flex:1,textAlign:"center"}}>
                      <input value={goalM} onChange={e=>setGoalM(e.target.value)}
                        type="number" min="0" max="59" placeholder="0"
                        style={{width:"100%",background:"rgba(255,255,255,0.03)",
                          border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                          padding:"14px",color:"#fff",fontSize:22,fontWeight:800,outline:"none",
                          fontFamily:"'Share Tech Mono',monospace",textAlign:"center"}}/>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:6}}>MINS</div>
                    </div>
                  </div>
                </>
              ):(
                <>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:10}}>
                    WEEKLY {(customUnit||METRICS[metric]?.unit||"units").toUpperCase()} GOAL
                  </div>
                  <input value={goalAmt} onChange={e=>setGoalAmt(e.target.value)}
                    type="number" min="0" placeholder="0"
                    style={{width:"100%",background:"rgba(255,255,255,0.03)",
                      border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                      padding:"14px",color:"#fff",fontSize:22,fontWeight:800,outline:"none",
                      fontFamily:"'Share Tech Mono',monospace",textAlign:"center"}}/>
                  {metric!=="custom"&&(
                    <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:8,textAlign:"center"}}>
                      e.g. {metric==="pages"?"50 pages / week":metric==="reps"?"100 reps / week":metric==="km"?"10 km / week":metric==="words"?"2000 words / week":"5 / week"}
                    </div>
                  )}
                </>
              )}
            </div>

            <button onClick={nextStep}
              style={{width:"100%",padding:"14px",background:SYS,border:"none",borderRadius:12,
                color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,cursor:"pointer",
                marginBottom:10}}>
              [ NEXT ]
            </button>
            <button onClick={nextStep}
              style={{width:"100%",padding:"12px",background:"transparent",
                border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,
                color:"#64748b",fontSize:11,fontFamily:"monospace",cursor:"pointer"}}>
              SKIP GOAL FOR NOW
            </button>
          </div>
        )}

        {/* Step 5 — First log */}
        {step===5&&(
          <div style={{animation:"fadeIn 0.4s ease"}}>
            <div style={{fontFamily:"monospace",fontSize:10,color:SYS,letterSpacing:3,marginBottom:8}}>
              STEP 5 OF 5
            </div>
            <div style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:6}}>
              Log your first entry
            </div>
            <div style={{fontSize:12,color:"#64748b",marginBottom:24}}>
              Did you already do <span style={{color:"#fff",fontWeight:700}}>{title}</span> today?
            </div>
            <div style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${SYS_BORDER}`,
              borderRadius:12,padding:"20px",marginBottom:24}}>
              {metric==="time"?(
                <>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:10}}>
                    HOW LONG TODAY?
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{flex:1,textAlign:"center"}}>
                      <input value={logH} onChange={e=>setLogH(e.target.value)}
                        type="number" min="0" placeholder="0"
                        style={{width:"100%",background:"rgba(255,255,255,0.03)",
                          border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                          padding:"14px",color:"#fff",fontSize:22,fontWeight:800,outline:"none",
                          fontFamily:"'Share Tech Mono',monospace",textAlign:"center"}}/>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:6}}>HOURS</div>
                    </div>
                    <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:24,color:"#64748b",paddingBottom:20}}>:</div>
                    <div style={{flex:1,textAlign:"center"}}>
                      <input value={logM} onChange={e=>setLogM(e.target.value)}
                        type="number" min="0" max="59" placeholder="0"
                        style={{width:"100%",background:"rgba(255,255,255,0.03)",
                          border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                          padding:"14px",color:"#fff",fontSize:22,fontWeight:800,outline:"none",
                          fontFamily:"'Share Tech Mono',monospace",textAlign:"center"}}/>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:6}}>MINS</div>
                    </div>
                  </div>
                </>
              ):(
                <>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:10}}>
                    HOW MANY {(customUnit||METRICS[metric]?.unit||"units").toUpperCase()} TODAY?
                  </div>
                  <input value={logAmt} onChange={e=>setLogAmt(e.target.value)}
                    type="number" min="0" placeholder="0"
                    style={{width:"100%",background:"rgba(255,255,255,0.03)",
                      border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,
                      padding:"14px",color:"#fff",fontSize:22,fontWeight:800,outline:"none",
                      fontFamily:"'Share Tech Mono',monospace",textAlign:"center"}}/>
                </>
              )}
            </div>
            {/* Saved targets so far */}
            {savedTasks.length>0&&(
              <div style={{marginBottom:12,padding:"10px 14px",background:"rgba(56,189,248,0.05)",
                border:`1px solid ${SYS_BORDER}`,borderRadius:10}}>
                <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:2,marginBottom:6}}>
                  TARGETS ADDED ({savedTasks.length})
                </div>
                {savedTasks.map((t,i)=>(
                  <div key={i} style={{fontSize:11,color:SYS,fontFamily:"monospace"}}>✓ {t.title}</div>
                ))}
              </div>
            )}

            {!logDone?(
              /* Phase 1 — Log entry */
              <>
                <button onClick={()=>{AudioEngine.log();setLogDone(true);}}
                  style={{width:"100%",padding:"14px",background:SYS,border:"none",borderRadius:12,
                    color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,
                    cursor:"pointer",marginBottom:10}}>
                  {(logAmt||(metric==="time"&&(logH||logM)))?"[ LOG ENTRY ]":"[ SKIP LOG ]"}
                </button>
              </>
            ):(
              /* Phase 2 — Add another or enter */
              <>
                <div style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${SYS_BORDER}`,
                  borderRadius:12,padding:"14px 16px",marginBottom:16,
                  fontFamily:"monospace",fontSize:11,color:SYS,textAlign:"center",letterSpacing:1}}>
                  ✓ TARGET SAVED
                </div>
                <button onClick={()=>{AudioEngine.click();handleAddAnother();}}
                  style={{width:"100%",padding:"14px",background:SYS_DIM,
                    border:`1px solid ${SYS_BORDER}`,borderRadius:12,
                    color:SYS,fontSize:12,fontFamily:"monospace",cursor:"pointer",
                    marginBottom:10,letterSpacing:2,fontWeight:800}}>
                  + ADD ANOTHER TARGET
                </button>
                <button onClick={()=>{AudioEngine.enterSystem();handleFinish(false);}}
                  style={{width:"100%",padding:"14px",background:SYS,border:"none",borderRadius:12,
                    color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,
                    cursor:"pointer",marginBottom:10}}>
                  [ ENTER SYSTEM ]
                </button>
                <button onClick={()=>handleFinish(true)}
                  style={{width:"100%",padding:"10px",background:"transparent",
                    border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,
                    color:"#64748b",fontSize:10,fontFamily:"monospace",cursor:"pointer"}}>
                  SKIP FOR NOW
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

